import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  ReactNode,
} from "react";

export type VideoPlayerRef = {
  play: () => Promise<void>;
  pause: () => void;
  seek: (time: number) => void;
  mute: () => void;
  unmute: () => void;
  togglePlay: () => void;
  toggleMute: () => void;
  getVideoElement: () => HTMLVideoElement | null;
};

export type VideoState = {
  isPlaying: boolean;
  isMuted: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  hasCaptions: boolean;
  showCaptions: boolean;
};

export type Props = {
  /** Video source URL */
  src: string;
  /** Poster image URL */
  poster?: string;
  /** Video title for accessibility */
  title?: string;
  /** Auto-play video */
  autoPlay?: boolean;
  /** Auto-play when muted */
  autoPlayOnMute?: boolean;
  /** Initial muted state */
  muted?: boolean;
  /** Loop video */
  loop?: boolean;
  /** Show native controls */
  nativeControls?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Callback when video ends */
  onEnded?: () => void;
  /** Callback when video plays */
  onPlay?: () => void;
  /** Callback when video pauses */
  onPause?: () => void;
  /** Callback when video state changes */
  onStateChange?: (state: VideoState) => void;
  /** Captions URL (VTT file) */
  captionsUrl?: string;
  /** Custom controls renderer */
  renderControls?: (props: {
    state: VideoState;
    actions: {
      play: () => Promise<void>;
      pause: () => void;
      seek: (time: number) => void;
      togglePlay: () => void;
      toggleMute: () => void;
      toggleCaptions: () => void;
    };
  }) => ReactNode;
  /** Custom play button renderer */
  renderPlayButton?: (props: {
    isPlaying: boolean;
    onClick: () => void;
  }) => ReactNode;
  /** Custom unmute overlay renderer */
  renderUnmuteOverlay?: (props: { onClick: () => void }) => ReactNode;
  /** Test ID for testing */
  "data-testid"?: string;
};

/**
 * Base video player component with no styling.
 * Handles video playback, state management, and captions.
 * Use render props to customize controls and overlays.
 *
 * @example
 * // In shared/ui/VideoPlayer.tsx
 * import { VideoPlayerBase, type Props as VideoPlayerBaseProps } from "@core/ui";
 *
 * export function VideoPlayer(props: VideoPlayerBaseProps) {
 *   return (
 *     <VideoPlayerBase
 *       {...props}
 *       renderControls={({ state, actions }) => (
 *         <div className="video-controls">
 *           <button onClick={actions.togglePlay}>
 *             {state.isPlaying ? 'Pause' : 'Play'}
 *           </button>
 *         </div>
 *       )}
 *     />
 *   );
 * }
 */
export const VideoPlayerBase = forwardRef<VideoPlayerRef, Props>(
  (
    {
      src,
      poster,
      title,
      autoPlay = false,
      autoPlayOnMute = false,
      muted: initialMuted = false,
      loop = false,
      nativeControls = false,
      className,
      onEnded,
      onPlay,
      onPause,
      onStateChange,
      captionsUrl,
      renderControls,
      renderPlayButton,
      renderUnmuteOverlay,
      "data-testid": testId = "video-player",
    },
    ref
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);
    const [isMuted, setIsMuted] = useState(autoPlayOnMute || initialMuted);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [hasCaptions, setHasCaptions] = useState(false);
    const [showCaptions, setShowCaptions] = useState(false);

    const shouldAutoPlay = autoPlay || (autoPlayOnMute && isMuted);

    // Expose imperative methods
    useImperativeHandle(ref, () => ({
      play: async () => {
        if (videoRef.current) {
          await videoRef.current.play();
        }
      },
      pause: () => {
        videoRef.current?.pause();
      },
      seek: (time: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
        }
      },
      mute: () => {
        if (videoRef.current) {
          videoRef.current.muted = true;
          setIsMuted(true);
        }
      },
      unmute: () => {
        if (videoRef.current) {
          videoRef.current.muted = false;
          setIsMuted(false);
        }
      },
      togglePlay: () => {
        if (videoRef.current) {
          if (isPlaying) {
            videoRef.current.pause();
          } else {
            videoRef.current.play();
          }
        }
      },
      toggleMute: () => {
        if (videoRef.current) {
          const newMuted = !isMuted;
          videoRef.current.muted = newMuted;
          setIsMuted(newMuted);
        }
      },
      getVideoElement: () => videoRef.current,
    }));

    // Video event handlers
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const handleTimeUpdate = () => {
        setCurrentTime(video.currentTime);
        const calculatedProgress = (video.currentTime / video.duration) * 100;
        setProgress(isNaN(calculatedProgress) ? 0 : calculatedProgress);
      };

      const handleLoadedMetadata = () => {
        setDuration(video.duration);
        setProgress(0);
      };

      const handlePlay = () => {
        setIsPlaying(true);
        onPlay?.();
      };

      const handlePause = () => {
        setIsPlaying(false);
        onPause?.();
      };

      video.addEventListener("timeupdate", handleTimeUpdate);
      video.addEventListener("loadedmetadata", handleLoadedMetadata);
      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);

      return () => {
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
      };
    }, [onPlay, onPause]);

    // Handle captions
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const handleTrackLoad = () => {
        const hasAvailableTracks = video.textTracks.length > 0;
        setHasCaptions(hasAvailableTracks);

        if (hasAvailableTracks) {
          const track = video.textTracks[0];
          track.mode = showCaptions ? "showing" : "hidden";
        }
      };

      video.addEventListener("loadedmetadata", handleTrackLoad);
      return () => {
        video.removeEventListener("loadedmetadata", handleTrackLoad);
      };
    }, [showCaptions]);

    // Update track mode when showCaptions changes
    useEffect(() => {
      const video = videoRef.current;
      if (!video || video.textTracks.length === 0) return;

      const track = video.textTracks[0];
      track.mode = showCaptions ? "showing" : "hidden";
    }, [showCaptions]);

    // Notify state changes
    useEffect(() => {
      onStateChange?.({
        isPlaying,
        isMuted,
        currentTime,
        duration,
        progress,
        hasCaptions,
        showCaptions,
      });
    }, [
      isPlaying,
      isMuted,
      currentTime,
      duration,
      progress,
      hasCaptions,
      showCaptions,
      onStateChange,
    ]);

    // Actions for render props
    const actions = {
      play: async () => {
        if (videoRef.current) {
          await videoRef.current.play();
        }
      },
      pause: () => {
        videoRef.current?.pause();
      },
      seek: (time: number) => {
        if (videoRef.current) {
          videoRef.current.currentTime = time;
        }
      },
      togglePlay: () => {
        if (videoRef.current) {
          if (isPlaying) {
            videoRef.current.pause();
          } else {
            videoRef.current.play();
          }
        }
      },
      toggleMute: () => {
        if (videoRef.current) {
          const newMuted = !isMuted;
          videoRef.current.muted = newMuted;
          setIsMuted(newMuted);
        }
      },
      toggleCaptions: () => {
        setShowCaptions(!showCaptions);
      },
    };

    const state: VideoState = {
      isPlaying,
      isMuted,
      currentTime,
      duration,
      progress,
      hasCaptions,
      showCaptions,
    };

    const handleUnmute = async () => {
      if (!videoRef.current) return;
      setHasInteracted(true);
      setIsMuted(false);
      videoRef.current.muted = false;
      videoRef.current.currentTime = 0;
      try {
        await videoRef.current.play();
      } catch (error) {
        console.error("Failed to play video:", error);
      }
    };

    const handleVideoClick = () => {
      if (autoPlayOnMute && isMuted && !hasInteracted) {
        handleUnmute();
        return;
      }
      actions.togglePlay();
    };

    return (
      <div
        className={className}
        data-testid={testId}
        onClick={handleVideoClick}
      >
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          autoPlay={shouldAutoPlay}
          muted={isMuted}
          loop={loop}
          playsInline
          controls={nativeControls}
          onEnded={onEnded}
          data-testid={`${testId}-video`}
          aria-label={title}
        >
          {captionsUrl && (
            <track
              kind="subtitles"
              src={captionsUrl}
              srcLang="en"
              label="English"
              default
            />
          )}
        </video>

        {/* Unmute overlay */}
        {autoPlayOnMute &&
          isMuted &&
          !hasInteracted &&
          renderUnmuteOverlay &&
          renderUnmuteOverlay({ onClick: handleUnmute })}

        {/* Play button */}
        {!isPlaying &&
          (!isMuted || hasInteracted) &&
          renderPlayButton &&
          renderPlayButton({
            isPlaying,
            onClick: () => {
              actions.togglePlay();
            },
          })}

        {/* Custom controls */}
        {renderControls && renderControls({ state, actions })}
      </div>
    );
  }
);

VideoPlayerBase.displayName = "VideoPlayerBase";

